import { useMemo, useState } from 'react';
import { Feuille } from './Feuille.jsx';
import { MONO, Titre } from './base.jsx';
import { candidats } from '../logique/adequation.js';
import { LIBELLE, STATUTS } from '../data/postes.js';

const TON = { bon: 'var(--acc2)', attention: 'var(--warn)', mauvais: 'var(--bad)' };

/**
 * « Qui joue ici ? »
 *
 * Les indisponibles ne sont pas grisés, ils sont ABSENTS : à vingt secondes, debout,
 * dans le bruit, on ne lit pas du gris, on lit des noms. Ils reviennent dès qu'on tape
 * quelque chose dans la recherche — parce qu'alors c'est qu'on cherche quelqu'un de
 * précis, et l'application n'a pas à décider qu'il n'a rien à faire là.
 *
 * Le champ de recherche est en tête et non en bas : le cas réel en tribune est
 * d'entendre un nom sans savoir à quel poste le chercher.
 */
export function FeuilleChoix({ ouverte, slot, effectif, dejaPlaces, onChoisir, onFermer }) {
  const [recherche, setRecherche] = useState('');
  const [deplies, setDeplies] = useState(() => new Set());

  const groupes = useMemo(
    () => (slot ? candidats(effectif, slot.code, { dejaPlaces, recherche }) : []),
    [effectif, slot, dejaPlaces, recherche]
  );

  const fermer = () => { setRecherche(''); setDeplies(new Set()); onFermer?.(); };

  return (
    <Feuille
      ouverte={ouverte}
      onFermer={fermer}
      titre="Qui joue ici ?"
      sousTitre={slot ? `${LIBELLE[slot.code] ?? slot.code} · ${slot.code}` : null}
      entete={<div style={{ padding: '0 14px 10px' }}>
        <input
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Chercher un nom ou un numéro…"
          // 16 px est un plancher, pas un choix esthétique : en dessous, Safari zoome
          // sur le champ à la mise au point et ne dézoome jamais.
          style={{
            width: '100%', height: 46, borderRadius: 13, padding: '0 14px', fontSize: 16,
            background: 'var(--surf2)', border: '1px solid var(--line)', outline: 'none',
          }}
          type="search"
          enterKeyHint="done"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
      </div>}
    >
      <div style={{ padding: '0 14px 20px' }}>
        {groupes.length === 0 && (
          <div style={{ padding: '24px 6px', color: 'var(--ink2)', fontSize: 13 }}>Personne ne correspond.</div>
        )}

        {groupes.map((g) => {
          const replie = g.replie && !deplies.has(g.titre);
          return (
            <section key={g.titre}>
              <button
                onClick={() => setDeplies((s) => { const n = new Set(s); n.has(g.titre) ? n.delete(g.titre) : n.add(g.titre); return n; })}
                disabled={!g.replie}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '14px 4px 8px' }}
              >
                <Titre>{g.titre.toUpperCase()}</Titre>
                <span style={{ fontSize: 10, fontFamily: MONO, color: 'var(--ink3)' }}>{g.joueurs.length}</span>
                {g.replie && <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink2)' }}>{replie ? 'Voir' : 'Masquer'}</span>}
              </button>

              {!replie && g.joueurs.map(({ joueur, valeur }) => {
                const st = STATUTS[joueur.statut];
                return (
                  <button
                    key={joueur.id}
                    onClick={() => { onChoisir?.(joueur); fermer(); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 11, width: '100%',
                      padding: 9, marginBottom: 6, borderRadius: 13, textAlign: 'left',
                      background: 'var(--surf2)', border: '1px solid var(--line)',
                      opacity: joueur.statut === 'ok' ? 1 : .72,
                    }}
                  >
                    <span style={{
                      width: 38, height: 38, flex: 'none', borderRadius: 11, background: 'var(--surf)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: MONO, fontWeight: 700, fontSize: 14,
                    }}>{joueur.numero ?? '·'}</span>

                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{joueur.nom}</span>
                      <span style={{ display: 'block', fontSize: 10.5, fontFamily: MONO, color: 'var(--ink2)', marginTop: 2 }}>
                        {joueur.poste}{joueur.alt?.length ? ` · ${joueur.alt.join(' ')}` : ''}
                        {joueur.statut !== 'ok' && ` · ${st.libelle}`}
                      </span>
                    </span>

                    <Jauge valeur={valeur} ton={joueur.statut === 'ok' ? null : TON[st.ton]} />
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>
    </Feuille>
  );
}

// Trois barres plutôt qu'un nombre : « 1,8 sur 2 » ne veut rien dire à personne, trois
// barres pleines se lisent sans être lues.
function Jauge({ valeur, ton }) {
  const n = valeur >= 1.8 ? 3 : valeur >= 1.2 ? 2 : valeur >= 0.6 ? 1 : 0;
  const couleur = ton ?? (n === 3 ? 'var(--acc2)' : n === 2 ? 'var(--acc)' : 'var(--ink3)');
  return (
    <span aria-label={`adéquation ${n} sur 3`} style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 18, flex: 'none' }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 4, height: 7 + i * 5, borderRadius: 2,
          background: i < n ? couleur : 'var(--line)',
        }} />
      ))}
    </span>
  );
}
